import React, { useEffect, useRef, useMemo } from 'react';
import moment from 'moment';
import { PercentInput } from '../percent-input/PercentInput';
import { CheckoutStatusDots } from '../checkout-status/CheckoutStatusDots';
import { ConfirmationModal } from '../confirmation-modal/ConfirmationModal';
import { Shop } from '@pdqdev/pdq-shared/dist/entities/Shop';
import Dropdown, { Option } from '../dropdown/Dropdown';
import { getCheckoutStatus, moveLiveCheckoutToTopOfTheList } from '@app/react-common/utils/CheckoutUtils';
import { Checkbox } from '../checkbox/Checkbox';
import {
  TrafficAllocationVariant,
  TrafficAllocationConfig,
  TrafficAllocationFormData,
} from './TrafficAllocationShared';

export interface ThemeTrafficAllocationInputProps {
  trafficAllocationConfig: TrafficAllocationConfig;
  onChange: (valid: boolean, variants: TrafficAllocationVariant[]) => void;
  viewMode?: boolean;
  showValidation?: boolean;
  shop: Shop;
  checkouts?: any[];
  onCheckoutGroupChange?: (themeId: string, checkoutGroupIds: number[]) => void;
  runningAbTestCheckoutIds?: number[];
  isCrossSite?: boolean;
}

export const ThemeTrafficAllocationInput: React.FC<ThemeTrafficAllocationInputProps> = ({
  trafficAllocationConfig,
  onChange,
  viewMode = false,
  showValidation,
  shop,
  checkouts = [],
  onCheckoutGroupChange,
  runningAbTestCheckoutIds = [],
  isCrossSite = false,
}) => {
  const [validationMessageForModal, setValidationMessageForModal] = React.useState<string>('');
  const [formData, setFormData] = React.useState<TrafficAllocationFormData>({
    variants: trafficAllocationConfig.variants.map((variant, idx) => {
      return {
        ...variant,
        internalId: idx,
      };
    }),
  });
  const prevDataRef = useRef<{ isValid: boolean; variants: TrafficAllocationVariant[] }>();

  const sortedCheckouts = useMemo(() => {
    if (!checkouts.length) return [];

    const sorted = [...checkouts];
    moveLiveCheckoutToTopOfTheList(sorted, runningAbTestCheckoutIds);
    return sorted;
  }, [checkouts, runningAbTestCheckoutIds]);

  // Initialize checkoutGroupAssignments from variants ONLY ONCE on mount
  // Use lazy initialization to extract initial state from props
  const [checkoutGroupAssignments, setCheckoutGroupAssignments] = React.useState<{ [themeId: string]: number[] }>(
    () => {
      const initialAssignments: { [themeId: string]: number[] } = {};
      trafficAllocationConfig.variants.forEach((variant) => {
        if (variant.checkoutGroupIds && variant.checkoutGroupIds.length > 0) {
          // Use "null" as string key for null externalId, otherwise use the id as string
          const themeId = variant.widgetExternalId === null ? 'null' : variant.widgetExternalId?.toString();
          if (themeId !== undefined) {
            initialAssignments[themeId] = variant.checkoutGroupIds;
          }
        }
      });
      return initialAssignments;
    },
  );

  useEffect(() => {
    const selectedVariants = formData.variants.filter((x: TrafficAllocationVariant) => x.isSelected);
    const percentageSum = selectedVariants.reduce((total: number, obj: TrafficAllocationVariant) => {
      return total + obj.percentageTraffic;
    }, 0);

    // For A/B testing, we need at least 2 variants selected and the traffic allocation must equal 100%
    const isValid = selectedVariants.length >= 2 && percentageSum === 100;

    // Merge checkoutGroupAssignments into variants before calling onChange
    const variantsWithCheckoutGroups = formData.variants.map((variant) => {
      // Use "null" as string key for null externalId, otherwise use the id as string
      const themeId = variant.widgetExternalId === null ? 'null' : variant.widgetExternalId?.toString();
      const checkoutGroupIds = themeId !== undefined ? checkoutGroupAssignments[themeId] : undefined;
      // Include checkoutGroupIds if it has been set (even if empty array)
      // This allows explicitly clearing selections
      return checkoutGroupIds !== undefined ? { ...variant, checkoutGroupIds } : variant;
    });

    // Only call onChange if values actually changed to prevent infinite loop
    const prevData = prevDataRef.current;
    const hasChanged =
      !prevData ||
      prevData.isValid !== isValid ||
      JSON.stringify(prevData.variants) !== JSON.stringify(variantsWithCheckoutGroups);

    if (hasChanged) {
      onChange(isValid, variantsWithCheckoutGroups);
      prevDataRef.current = { isValid, variants: variantsWithCheckoutGroups };
    }
  }, [formData, checkoutGroupAssignments, onChange]);

  const setVariantPercentage = (id: number, percentage: number) => {
    setFormData((oldFormData) => {
      return {
        variants: oldFormData.variants.map((variant) => {
          if (variant.internalId === id) {
            return {
              ...variant,
              percentageTraffic: percentage,
            };
          }
          return variant;
        }),
      };
    });
  };

  const toggleSelected = (id: number) => {
    setFormData((prevData) => {
      const clickedVariant = prevData.variants.find((v) => v.internalId === id);
      if (!clickedVariant) return prevData;

      const currentSelectedCount = prevData.variants.filter((v) => v.isSelected).length;
      const maxVariants = 4; // Cross-Site supports up to 4 variants

      // Prevent selecting more than max allowed
      if (!clickedVariant.isSelected && currentSelectedCount === maxVariants) {
        setValidationMessageForModal(`Currently we only support ${maxVariants} variants for testing.`);
        return prevData;
      }

      const newSelectedCount = clickedVariant.isSelected ? currentSelectedCount - 1 : currentSelectedCount + 1;

      // For 1-2 variants, use 50% default. For 3+, distribute evenly
      const basePercentage = newSelectedCount < 3 ? 50 : Math.floor(100 / newSelectedCount);
      const remainder = newSelectedCount < 3 ? 0 : 100 - basePercentage * newSelectedCount;

      let assignedCount = 0;
      const totalToAssign = newSelectedCount;

      return {
        variants: prevData.variants.map((variant) => {
          // Toggle the clicked variant
          if (variant.internalId === id) {
            const isLastToAssign = assignedCount === totalToAssign - 1;
            const percentage = variant.isSelected
              ? 0
              : isLastToAssign && newSelectedCount >= 3
                ? basePercentage + remainder
                : basePercentage;
            if (!variant.isSelected) assignedCount++;
            return {
              ...variant,
              isSelected: !variant.isSelected,
              percentageTraffic: percentage,
            };
          }
          // Update all other selected variants with new percentage distribution
          if (variant.isSelected) {
            const isLastToAssign = assignedCount === totalToAssign - 1;
            const percentage = isLastToAssign && newSelectedCount >= 3 ? basePercentage + remainder : basePercentage;
            assignedCount++;
            return { ...variant, percentageTraffic: percentage };
          }
          return variant;
        }),
      };
    });
  };

  return (
    <div className="theme-traffic-allocation-input">
      {formData.variants.map((variant) => {
        // Use "null" as string key for null externalId, otherwise use the id as string
        const themeId = variant.widgetExternalId === null ? 'null' : variant.widgetExternalId?.toString() || '';

        return (
          <div
            key={variant.internalId}
            className={`theme-traffic-allocation-input__theme-variant-wrapper ${variant.isSelected ? 'theme-traffic-allocation-input__theme-variant-wrapper--selected' : ''}`}>
            <div
              className={`theme-traffic-allocation-input__item theme-traffic-allocation-input__item--theme ${!viewMode && 'theme-traffic-allocation-input__clickable'}`}
              onMouseDown={(e) => {
                if (viewMode || (e.target as HTMLElement).closest('a')) {
                  return;
                }
                toggleSelected(variant.internalId);
              }}>
              <div>
                <Checkbox checked={variant.isSelected} onChange={() => {}} disabled={viewMode} />
              </div>
              {/* TODO: Re-enable when Shopify API provides theme preview images
              <div className="theme-traffic-allocation-input__item__thumbnail">
                <img
                  src={variant.previewUrl}
                  alt={variant.name}
                  className="theme-traffic-allocation-input__item__thumbnail-image"
                />
              </div>
              */}
              <div className="theme-traffic-allocation-input__item__info">
                <div className={`theme-traffic-allocation-input__item__info__title`}>
                  {variant.widgetExternalId && isCrossSite ? (
                    <a
                      href={`https://admin.shopify.com/store/${shop?.name?.replace('.myshopify.com', '')}/themes/${variant.widgetExternalId}/editor`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: 'underline', cursor: 'pointer', color: 'inherit' }}>
                      {variant.name}
                    </a>
                  ) : (
                    variant.name
                  )}
                </div>
                <div className={`theme-traffic-allocation-input__item__info__description ${viewMode && 'disabled'}`}>
                  {variant.updatedAt
                    ? `Last saved: ${moment(variant.updatedAt).format('MMM D [at] h:mm a')}`
                    : variant.description}
                </div>
              </div>
              <div className="theme-traffic-allocation-input__item__percentage">
                <PercentInput
                  value={variant.percentageTraffic}
                  setValue={(value: number) => {
                    if (!viewMode) setVariantPercentage(variant.internalId, value);
                  }}
                  disabled={viewMode || (variant.isSelected ? false : true)}
                  setValid={() => {}}
                  viewMode={viewMode}
                />
              </div>
            </div>
            <div className="theme-traffic-allocation-input__item__checkout-group">
              <div className="theme-traffic-allocation-input__item__checkout-group-header">
                <label className="theme-traffic-allocation-input__item__checkout-group-label">
                  Assigned Checkout Group
                </label>
              </div>
              <Dropdown
                options={sortedCheckouts.map((checkout) => {
                  const status = getCheckoutStatus(checkout, runningAbTestCheckoutIds);
                  return {
                    label: checkout.name,
                    value: checkout.id,
                    customContent: (
                      <div className="theme-traffic-allocation-input__item__dropdown-content">
                        <div className="theme-traffic-allocation-input__item__dropdown-status">
                          <CheckoutStatusDots status={status} showTooltip={false} />
                        </div>
                        <span className="theme-traffic-allocation-input__item__dropdown-label">{checkout.name}</span>
                      </div>
                    ),
                  };
                })}
                placeholder="Select a group (Optional)"
                multiSelect={true}
                showSelectUnselectOptions={true}
                preSelectedOptions={
                  checkoutGroupAssignments[themeId]
                    ?.map((checkoutId: number) => {
                      const checkout = checkouts?.find((c) => c.id === checkoutId);
                      return checkout
                        ? {
                            label: checkout.name,
                            value: checkout.id,
                          }
                        : null;
                    })
                    .filter(Boolean) || []
                }
                onSelect={(selectedOptions: Option[]) => {
                  if (onCheckoutGroupChange) {
                    const selectedIds = selectedOptions.map((opt) => opt.value as number);
                    setCheckoutGroupAssignments((prev: { [themeId: string]: number[] }) => ({
                      ...prev,
                      [themeId]: selectedIds,
                    }));
                    onCheckoutGroupChange(themeId, selectedIds);
                  }
                }}
                disabled={false}
                disabledOptionsOnly={viewMode}
              />
              <div className="theme-traffic-allocation-input__no-group-disclaimer">
                If not group is assigned or matched, checkout follows your live shipping rate groups.
              </div>
            </div>
          </div>
        );
      })}
      {validationMessageForModal && (
        <ConfirmationModal
          title={validationMessageForModal}
          buttonText={'Ok'}
          isOpen={validationMessageForModal.length > 0}
          mainContent={'Please unselect one of your variants to select this one.'}
          onClose={() => {
            setValidationMessageForModal('');
          }}
          onSuccess={() => {
            setValidationMessageForModal('');
          }}
          hideCancel={true}
        />
      )}
    </div>
  );
};
